/**
 * Detection helper for the backend "organisation has no ready Stripe account"
 * rejection. The backend raises it when a payment intent/session is created for
 * an org that has not completed Stripe Connect onboarding:
 *   - 400 from POST /api/v2/payment/create and /create-guest
 *   - 422 from the class QR, booking pay-link, and trial-deal intent paths
 * Newer backends attach errorCode 'ORG_PAYMENTS_NOT_READY'; the message pattern
 * covers responses from a backend deployed before that code existed.
 */
const NOT_READY_PATTERN = /has not set up (a )?payment|payment account is not yet active|not ready to receive payments/i;

const ORG_PAYMENTS_NOT_READY = 'ORG_PAYMENTS_NOT_READY';

/**
 * Accepts either a thrown Error (from api wrappers that throw) or an
 * { ok, status, data } response object (from wrappers that return raw responses).
 * @returns {boolean} true when the failure means "this org cannot take card payments yet"
 */
export const isOrgPaymentsNotReady = (source) => {
    if (!source) return false;
    if (source.errorCode === ORG_PAYMENTS_NOT_READY) return true;
    if (source.data?.errorCode === ORG_PAYMENTS_NOT_READY || source.data?.code === ORG_PAYMENTS_NOT_READY) return true;
    const message = source.message || source.data?.message || '';
    return NOT_READY_PATTERN.test(message);
};

/**
 * Friendly copy for the payer-facing failure state.
 * @param {string} sellerName - org/school/instructor display name (falls back to a generic noun)
 * @param {string} contactWho - who the payer should contact, e.g. "the school" or "your instructor"
 */
export const orgPaymentsNotReadyMessage = (sellerName, contactWho = 'them') =>
    `${sellerName || 'The seller'} hasn't finished setting up online payments yet. ` +
    `Please contact ${contactWho} directly to arrange payment.`;
